import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { orderService } from "../../services/orderService";
import {
    Order,
    CreateOrderRequest,
    OrderSummary,
    SearchParams,
    OrderSearchParams,
} from "../../types";

// Define the initial state interface
interface OrdersState {
    orders: Order[];
    selectedOrder: Order | null;
    orderSummary: OrderSummary | null;
    recentOrders: Order[];
    isLoading: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    isDeleting: boolean;
    isFetchingSummary: boolean;
    error: string | null;
    searchParams: SearchParams;
    orderSearchParams: OrderSearchParams;
    totalCount: number;
}

// Define the initial state
const initialState: OrdersState = {
    orders: [],
    selectedOrder: null,
    orderSummary: null,
    recentOrders: [],
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingSummary: false,
    error: null,
    searchParams: { skip: 0, limit: 50 },
    orderSearchParams: {
        skip: 0,
        limit: 50,
        sort_by: "order_date",
        sort_order: "desc",
    },
    totalCount: 0,
};

// Async thunks
export const fetchOrders = createAsyncThunk(
    "orders/fetchOrders",
    async (params: SearchParams = {}, { rejectWithValue }) => {
        try {
            const response = await orderService.getOrders(params);
            return { orders: response.items, params };
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch orders");
        }
    }
);

export const fetchOrderById = createAsyncThunk(
    "orders/fetchOrderById",
    async (orderId: string, { rejectWithValue }) => {
        try {
            const order = await orderService.getOrderById(orderId);
            return order;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch order");
        }
    }
);

export const fetchOrderSummary = createAsyncThunk(
    "orders/fetchOrderSummary",
    async (_, { rejectWithValue }) => {
        try {
            const summary = await orderService.getOrderSummary();
            return summary;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch order summary"
            );
        }
    }
);

export const fetchRecentOrders = createAsyncThunk(
    "orders/fetchRecentOrders",
    async (limit: number = 10, { rejectWithValue }) => {
        try {
            const response = await orderService.getOrders({ limit });
            return response.items;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch recent orders"
            );
        }
    }
);

export const searchOrders = createAsyncThunk(
    "orders/searchOrders",
    async (searchParams: OrderSearchParams, { rejectWithValue }) => {
        try {
            const response = await orderService.searchOrders(searchParams);
            return { orders: response.items, total: response.total, searchParams };
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to search orders");
        }
    }
);

export const createOrder = createAsyncThunk(
    "orders/createOrder",
    async (orderData: CreateOrderRequest, { rejectWithValue, dispatch }) => {
        try {
            const newOrder = await orderService.createOrder(orderData);
            // Refresh the orders list and summary
            dispatch(fetchOrders({}));
            dispatch(fetchOrderSummary());
            return newOrder;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to create order");
        }
    }
);

export const updateOrderStatus = createAsyncThunk(
    "orders/updateOrderStatus",
    async (
        { orderId, status }: { orderId: string; status: Order["status"] },
        { rejectWithValue, dispatch }
    ) => {
        try {
            const updatedOrder = await orderService.updateOrderStatus(
                orderId,
                status
            );
            // Refresh the orders list and summary
            dispatch(fetchOrders({}));
            dispatch(fetchOrderSummary());
            return updatedOrder;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to update order status"
            );
        }
    }
);

export const deleteOrder = createAsyncThunk(
    "orders/deleteOrder",
    async (orderId: string, { rejectWithValue, dispatch }) => {
        try {
            await orderService.cancelOrder(orderId, "cancelled");
            // Refresh the orders list and summary
            dispatch(fetchOrders({}));
            dispatch(fetchOrderSummary());
            return orderId;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to delete order");
        }
    }
);

// Create the orders slice
const ordersSlice = createSlice({
    name: "orders",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setSelectedOrder: (state, action: PayloadAction<Order | null>) => {
            state.selectedOrder = action.payload;
        },
        updateSearchParams: (
            state,
            action: PayloadAction<Partial<SearchParams>>
        ) => {
            state.searchParams = { ...state.searchParams, ...action.payload };
        },
        updateOrderSearchParams: (
            state,
            action: PayloadAction<Partial<OrderSearchParams>>
        ) => {
            state.orderSearchParams = { ...state.orderSearchParams, ...action.payload };
        },
        clearSelectedOrder: (state) => {
            state.selectedOrder = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch orders
            .addCase(fetchOrders.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchOrders.fulfilled, (state, action) => {
                state.isLoading = false;
                state.orders = action.payload.orders;
                state.searchParams = {
                    ...state.searchParams,
                    ...action.payload.params,
                };
                state.totalCount = action.payload.orders.length;
                state.error = null;
            })
            .addCase(fetchOrders.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch order by ID
            .addCase(fetchOrderById.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchOrderById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.selectedOrder = action.payload;
                state.error = null;
            })
            .addCase(fetchOrderById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch order summary
            .addCase(fetchOrderSummary.pending, (state) => {
                state.isFetchingSummary = true;
                state.error = null;
            })
            .addCase(fetchOrderSummary.fulfilled, (state, action) => {
                state.isFetchingSummary = false;
                state.orderSummary = action.payload;
                state.error = null;
            })
            .addCase(fetchOrderSummary.rejected, (state, action) => {
                state.isFetchingSummary = false;
                state.error = action.payload as string;
            })
            // Fetch recent orders
            .addCase(fetchRecentOrders.fulfilled, (state, action) => {
                state.recentOrders = action.payload;
            })
            // Create order
            .addCase(createOrder.pending, (state) => {
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createOrder.fulfilled, (state) => {
                state.isCreating = false;
                state.error = null;
            })
            .addCase(createOrder.rejected, (state, action) => {
                state.isCreating = false;
                state.error = action.payload as string;
            })
            // Update order status
            .addCase(updateOrderStatus.pending, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateOrderStatus.fulfilled, (state) => {
                state.isUpdating = false;
                state.error = null;
            })
            .addCase(updateOrderStatus.rejected, (state, action) => {
                state.isUpdating = false;
                state.error = action.payload as string;
            })
            // Delete order
            .addCase(deleteOrder.pending, (state) => {
                state.isDeleting = true;
                state.error = null;
            })
            .addCase(deleteOrder.fulfilled, (state) => {
                state.isDeleting = false;
                state.error = null;
            })
            .addCase(deleteOrder.rejected, (state, action) => {
                state.isDeleting = false;
                state.error = action.payload as string;
            })
            // Search orders
            .addCase(searchOrders.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(searchOrders.fulfilled, (state, action) => {
                state.isLoading = false;
                state.orders = action.payload.orders;
                state.totalCount = action.payload.total;
                state.orderSearchParams = {
                    ...state.orderSearchParams,
                    ...action.payload.searchParams,
                };
                state.error = null;
            })
            .addCase(searchOrders.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

// Export actions
export const {
    clearError,
    setSelectedOrder,
    updateSearchParams,
    updateOrderSearchParams,
    clearSelectedOrder,
} = ordersSlice.actions;

// Export selectors
export const selectOrders = (state: any) => state.orders.orders;
export const selectSelectedOrder = (state: any) => state.orders.selectedOrder;
export const selectOrderSummary = (state: any) => state.orders.orderSummary;
export const selectRecentOrders = (state: any) => state.orders.recentOrders;
export const selectOrdersLoading = (state: any) => state.orders.isLoading;
export const selectOrdersError = (state: any) => state.orders.error;
export const selectOrdersSearchParams = (state: any) =>
    state.orders.searchParams;
export const selectOrderSearchParams = (state: any) =>
    state.orders.orderSearchParams;
export const selectOrdersTotalCount = (state: any) => state.orders.totalCount;
export const selectOrdersCreating = (state: any) => state.orders.isCreating;
export const selectOrdersUpdating = (state: any) => state.orders.isUpdating;
export const selectOrdersDeleting = (state: any) => state.orders.isDeleting;
export const selectOrderSummaryLoading = (state: any) =>
    state.orders.isFetchingSummary;

// Export reducer
export default ordersSlice.reducer;
